/**
 * Client poll guard for the Cloudflare Worker.
 *
 * One open dashboard fans out ~24 API polls. Left running in background
 * tabs, that is the request volume on the Worker. Anything faster than
 * 10s is clamped, hidden tabs pause, and consecutive failures stretch
 * the interval (they never shorten it).
 */

export const MIN_API_POLL_MS = 10_000;
const BACKOFF_CAP_FACTOR = 8;

export type PollResult = "ok" | "fail" | "skip";

export function pageHidden(): boolean {
  return typeof document !== "undefined" && document.hidden === true;
}

/** Raise sub-10s intervals to the floor. Slower callers stay as given. */
export function clampPollMs(pollMs: number): number {
  if (!Number.isFinite(pollMs) || pollMs < MIN_API_POLL_MS) return MIN_API_POLL_MS;
  return pollMs;
}

/**
 * Delay until the next poll.
 * `null` means the tab is hidden — do not schedule a timer.
 * Failures multiply the base by 2, 4, 8 and then stay at 8×.
 */
export function nextPollDelayMs(
  baseMs: number,
  consecutiveFailures: number,
  hidden = pageHidden(),
): number | null {
  if (hidden) return null;
  const base = clampPollMs(baseMs);
  const fails = Number.isFinite(consecutiveFailures)
    ? Math.max(0, Math.floor(consecutiveFailures))
    : 0;
  if (fails === 0) return base;
  const factor = Math.min(2 ** fails, BACKOFF_CAP_FACTOR);
  return base * factor;
}

export interface GuardedPoll {
  /** Run now, cancelling a pending wait. Explicit refresh. */
  kick: () => void;
  stop: () => void;
}

/**
 * setTimeout chain instead of setInterval so the delay can grow after
 * errors and disappear while the tab is hidden.
 */
export function startGuardedPoll(
  run: () => Promise<PollResult>,
  baseMs: number,
): GuardedPoll {
  let stopped = false;
  let running = false;
  let queued = false;
  let fails = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const clear = () => {
    if (timer !== undefined) {
      clearTimeout(timer);
      timer = undefined;
    }
  };

  const arm = () => {
    clear();
    if (stopped) return;
    const delay = nextPollDelayMs(baseMs, fails);
    if (delay === null) return;
    timer = setTimeout(() => {
      void tick();
    }, delay);
  };

  const tick = async () => {
    if (stopped || pageHidden()) return;
    if (running) {
      queued = true;
      return;
    }
    running = true;
    let result: PollResult = "fail";
    try {
      result = await run();
    } catch {
      result = "fail";
    } finally {
      running = false;
    }
    if (stopped) return;
    if (result === "ok") fails = 0;
    else if (result === "fail") fails += 1;
    if (queued) {
      queued = false;
      void tick();
      return;
    }
    arm();
  };

  const onVis = () => {
    if (pageHidden()) {
      clear();
      return;
    }
    clear();
    void tick();
  };

  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onVis);
  }
  void tick();

  return {
    kick() {
      if (stopped) return;
      clear();
      if (running) {
        queued = true;
        return;
      }
      void tick();
    },
    stop() {
      stopped = true;
      queued = false;
      clear();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVis);
      }
    },
  };
}
